import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";

const JSBARCODE_SRC = "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js";

const BARCODE_OPTIONS = {
    format: "CODE128",
    displayValue: true,
    fontSize: 14,
    height: 70,
    margin: 8,
    width: 2,
};

let jsBarcodePromise;

const ensureJsBarcode = () => {
    if (typeof window !== "undefined" && window.JsBarcode) {
        return Promise.resolve(window.JsBarcode);
    }
    if (!jsBarcodePromise) {
        jsBarcodePromise = new Promise((resolve, reject) => {
            const existing = document.querySelector("script[data-jsbarcode='true']");
            if (existing) {
                existing.addEventListener("load", () => resolve(window.JsBarcode));
                existing.addEventListener("error", () => reject(new Error("Unable to load barcode renderer")));
                return;
            }
            const script = document.createElement("script");
            script.src = JSBARCODE_SRC;
            script.async = true;
            script.dataset.jsbarcode = "true";
            script.onload = () => resolve(window.JsBarcode);
            script.onerror = () => reject(new Error("Unable to load barcode renderer"));
            document.body.appendChild(script);
        });
    }
    return jsBarcodePromise;
};

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

export const getCollectedBarcodeIds = (row) => {
    const raw = row?.collectedSampleIds;
    const ids = [];

    if (Array.isArray(raw)) {
        raw.forEach((item) => {
            if (typeof item === "string" && item.trim()) {
                ids.push(item.trim());
            } else if (item && typeof item === "object") {
                const id = item.barcodeId || item.sampleBarcodeId || item.id;
                if (id) ids.push(String(id).trim());
            }
        });
    }

    if (ids.length === 0 && row?.barcodeId) {
        ids.push(String(row.barcodeId).trim());
    }

    return [...new Set(ids.filter(Boolean))];
};

const drawBarcodes = (JsBarcode, barcodeIds, idPrefix) => {
    barcodeIds.forEach((id, index) => {
        const node = document.getElementById(`${idPrefix}-${index}`);
        if (!node || !JsBarcode) return;
        JsBarcode(node, id, BARCODE_OPTIONS);
        node.setAttribute("width", "100%");
        node.removeAttribute("height");
        node.style.display = "block";
        node.style.margin = "0 auto";
    });
};

const printAllBarcodes = ({ barcodeIds, orderBarcodeId, patientName, testName, sampleName }) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.left = "-9999px";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) {
        iframe.remove();
        toast.warning("Unable to open the print preview");
        return;
    }

    const cards = barcodeIds
        .map(
            (_, index) => `
                <div class="card">
                    <svg id="print-barcode-${index}"></svg>
                </div>
            `
        )
        .join("");

    const meta = [
        orderBarcodeId ? `Order: ${escapeHtml(orderBarcodeId)}` : "",
        patientName ? `Patient: ${escapeHtml(patientName)}` : "",
        testName ? `Test: ${escapeHtml(testName)}` : "",
        sampleName ? `Sample: ${escapeHtml(sampleName)}` : "",
    ]
        .filter(Boolean)
        .join(" · ");

    doc.open();
    doc.write(`<!DOCTYPE html>
        <html>
            <head>
                <title>Sample Barcodes${orderBarcodeId ? ` - ${escapeHtml(orderBarcodeId)}` : ""}</title>
                <style>
                    @page { size: A4; margin: 12mm; }
                    body { font-family: Arial, sans-serif; margin: 0; color: #111; }
                    h1 { font-size: 16px; margin: 0 0 6px; }
                    .meta { font-size: 12px; color: #333; margin-bottom: 16px; }
                    .grid {
                        display: flex;
                        flex-wrap: wrap;
                        justify-content: center;
                        gap: 12px;
                    }
                    .card {
                        width: calc(50% - 6px);
                        border: 1px solid #d1d5db;
                        border-radius: 8px;
                        padding: 12px 16px;
                        box-sizing: border-box;
                        page-break-inside: avoid;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }
                    svg { display: block; width: 100%; height: auto; margin: 0 auto; }
                </style>
            </head>
            <body>
                <h1>Sample Barcodes</h1>
                ${meta ? `<div class="meta">${meta}</div>` : ""}
                <div class="grid">${cards}</div>
                <script src="${JSBARCODE_SRC}"></script>
                <script>
                    window.onload = function () {
                        var ids = ${JSON.stringify(barcodeIds)};
                        ids.forEach(function (id, index) {
                            var node = document.getElementById("print-barcode-" + index);
                            JsBarcode(node, id, ${JSON.stringify(BARCODE_OPTIONS)});
                            node.setAttribute("width", "100%");
                            node.removeAttribute("height");
                        });
                        window.focus();
                        window.print();
                    };
                </script>
            </body>
        </html>`);
    doc.close();

    const removeFrame = () => {
        if (iframe.parentNode) iframe.remove();
    };
    iframe.contentWindow?.addEventListener("afterprint", removeFrame, { once: true });
};

export default function BarcodePreviewModal({ show, onHide, row }) {
    const { t } = useTranslation();
    const barcodeIds = useMemo(() => getCollectedBarcodeIds(row), [row]);
    const barcodeKey = barcodeIds.join("|");
    const [renderError, setRenderError] = useState("");

    useEffect(() => {
        if (!show || barcodeIds.length === 0) return undefined;

        let active = true;
        setRenderError("");

        ensureJsBarcode()
            .then((JsBarcode) => {
                if (!active) return;
                requestAnimationFrame(() => {
                    if (!active) return;
                    drawBarcodes(JsBarcode, barcodeIds, "preview-barcode");
                });
            })
            .catch((error) => {
                console.log(error);
                if (active) setRenderError("Barcode preview could not be rendered.");
            });

        return () => {
            active = false;
        };
    }, [show, barcodeKey, barcodeIds]);

    const handlePrint = () => {
        if (barcodeIds.length === 0) {
            toast.warning(t("Barcode ID not available"));
            return;
        }
        printAllBarcodes({
            barcodeIds,
            orderBarcodeId: row?.orderBarcodeId,
            patientName: row?.patientName,
            testName: row?.testName,
            sampleName: row?.sampleName,
        });
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t("Sample Barcodes")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <div className="fw-semibold">{row?.orderBarcodeId || t("Test order")}</div>
                    <small className="text-muted">
                        {[row?.patientName, row?.testName, row?.sampleName].filter(Boolean).join(" · ") || "—"}
                    </small>
                    <div className="text-muted small mt-1">
                        {barcodeIds.length} {t("samples")}
                    </div>
                </div>

                {barcodeIds.length === 0 ? (
                    <div className="text-muted">{t("No barcodes found for this test order.")}</div>
                ) : (
                    <div className="d-flex flex-wrap justify-content-center" style={{ gap: "16px" }}>
                        {barcodeIds.map((id, index) => (
                            <div
                                key={id}
                                className="border rounded"
                                style={{
                                    flex: "1 1 280px",
                                    maxWidth: "340px",
                                    background: "#fff",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "12px 16px",
                                    boxSizing: "border-box",
                                }}
                            >
                                <svg
                                    id={`preview-barcode-${index}`}
                                    style={{ display: "block", width: "100%", height: "auto", margin: "0 auto" }}
                                />
                                {renderError ? <div className="fw-semibold mt-2">{id}</div> : null}
                            </div>
                        ))}
                    </div>
                )}
                {renderError ? <div className="text-danger small mt-3">{renderError}</div> : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t("Close")}
                </Button>
                <Button variant="primary" onClick={handlePrint} disabled={barcodeIds.length === 0}>
                    {t("Print")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
